var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { ChevronDown, ChevronUp } from '@sumup/icons';
import { uniqueId } from '../../util/id';
import styled from '../../styles/styled';
import { typography, inputOutline } from '../../styles/style-mixins';
import { useClickEvent } from '../../hooks/useClickEvent';
import { FieldWrapper, FieldLabel, FieldLabelText, FieldValidationHint, } from '../FieldAtoms';
import { AccessibilityError } from '../../util/errors';
const wrapperStyles = css `
  color: var(--cui-fg-normal);
  display: block;
  position: relative;
`;
const SelectWrapper = styled('div')(wrapperStyles);
const selectBaseStyles = ({ theme }) => css `
  appearance: none;
  cursor: pointer;
  background-color: var(--cui-bg-normal);
  outline: none;
  border: 0;
  border-radius: ${theme.borderRadius.byte};
  box-shadow: none;
  color: var(--cui-fg-normal);
  margin: 0;
  padding-top: ${theme.spacings.kilo};
  padding-right: ${theme.spacings.exa};
  padding-bottom: ${theme.spacings.kilo};
  padding-left: ${theme.spacings.mega};
  position: relative;
  width: 100%;
  z-index: ${theme.zIndex.input};
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: box-shadow ${theme.transitions.default},
    padding ${theme.transitions.default};

  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000;
  }

  &::-ms-expand {
    display: none;
  }
`;
const selectInvalidStyles = ({ theme, invalid, disabled, }) => invalid &&
    !disabled &&
    css `
    padding-right: ${theme.spacings.zetta};
  `;
const selectPrefixStyles = ({ theme, hasPrefix }) => hasPrefix &&
    css `
    padding-left: ${theme.spacings.exa};
  `;
const SelectElement = styled.select(typography('one'), selectBaseStyles, selectInvalidStyles, selectPrefixStyles, inputOutline);
/**
 * Used with css prop directly, so it does not require prop
 * destructuring.
 */
const prefixStyles = (theme) => css `
  display: block;
  position: absolute;
  z-index: ${theme.zIndex.input + 1};
  height: ${theme.spacings.exa};
  width: ${theme.spacings.exa};
  padding: ${theme.spacings.mega};
  pointer-events: none;
`;
const iconBaseStyles = ({ theme }) => css `
  color: var(--cui-fg-subtle);
  display: block;
  z-index: ${theme.zIndex.input + 1};
  pointer-events: none;
  position: absolute;
  height: ${theme.spacings.exa};
  width: ${theme.spacings.exa};
  top: 0;
  right: 0;
  padding: ${theme.spacings.mega};
`;
const iconActiveStyles = () => css `
  select:active ~ & {
    display: none;
  }
`;
const iconInactiveStyles = () => css `
  select:not(:active) ~ & {
    display: none;
  }
`;
const IconActive = styled(ChevronDown)(iconBaseStyles, iconActiveStyles);
const IconInactive = styled(ChevronUp)(iconBaseStyles, iconInactiveStyles);
/**
 * A native select component.
 */
export const Select = forwardRef((_a, ref) => {
    var { value, defaultValue, placeholder, disabled, invalid, required, options, children, 'renderPrefix': RenderPrefix, validationHint, optionalLabel, label, hideLabel, className, style, 'id': customId, onChange, tracking, 'aria-describedby': descriptionId } = _a, props = __rest(_a, ["value", "defaultValue", "placeholder", "disabled", "invalid", "required", "options", "children", 'renderPrefix', "validationHint", "optionalLabel", "label", "hideLabel", "className", "style", 'id', "onChange", "tracking", 'aria-describedby']);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('Select', 'The `label` prop is missing. Pass `hideLabel` if you intend to hide the label visually.');
    }
    const id = customId || uniqueId('select_');
    const validationHintId = uniqueId('validation-hint_');
    const descriptionIds = `${descriptionId ? `${descriptionId} ` : ''}${validationHintId}`;
    const prefix = RenderPrefix && (_jsx(RenderPrefix, { css: prefixStyles, value: value }));
    const hasPrefix = Boolean(prefix);
    const handleChange = useClickEvent(onChange, tracking, 'select');
    return (_jsxs(FieldWrapper, Object.assign({ className: className, style: style, disabled: disabled }, { children: [_jsx(FieldLabel, Object.assign({ htmlFor: id }, { children: _jsx(FieldLabelText, { label: label, hideLabel: hideLabel, optionalLabel: optionalLabel, required: required }) })), _jsxs(SelectWrapper, { children: [prefix, _jsxs(SelectElement, Object.assign({ id: id, value: value, ref: ref, "aria-describedby": descriptionIds, invalid: invalid, "aria-invalid": invalid && 'true', required: required, disabled: disabled, hasPrefix: hasPrefix, defaultValue: defaultValue }, props, { onChange: handleChange }, { children: [!value && !defaultValue && (
                            /**
                             * We need a key here just like when mapping over options.
                             * We're prefixing the key with an underscore to avoid clashes
                             * with option values.
                             */
                            _jsx("option", Object.assign({ value: "" }, { children: placeholder }), "_placeholder")), children ||
                                (options &&
                                    options.map((_a) => {
                                        var { label: optionLabel } = _a, rest = __rest(_a, ["label"]);
                                        return (_jsx("option", Object.assign({}, rest, { children: optionLabel }), rest.value));
                                    }))] })), _jsx(IconActive, { size: "16", "aria-hidden": "true" }), _jsx(IconInactive, { size: "16", "aria-hidden": "true" })] }), _jsx(FieldValidationHint, { id: validationHintId, disabled: disabled, invalid: invalid, validationHint: validationHint })] })));
});
Select.displayName = 'Select';
